import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Typography,
} from "@mui/material";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { format, eachDayOfInterval, parseISO } from "date-fns";
import { COLORS } from "./constants";

function getDateStr(item) {
  const raw = item.start_time || item.date || item.created_at;
  if (!raw) return null;
  try {
    return format(parseISO(raw), "yyyy-MM-dd");
  } catch {
    return null;
  }
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        background: "#fff",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "10px",
        p: 1.5,
        boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
      }}
    >
      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.5}>
        {label}
      </Typography>
      {payload.map((entry) => (
        <Box key={entry.name} display="flex" alignItems="center" gap={1} mb={0.3}>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: entry.color }} />
          <Typography variant="caption" color="text.primary">
            {entry.name}: <strong>{entry.value}</strong>
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default function ActivityOverviewChart({
  activities = [],
  calls = [],
  fromDate,
  toDate,
  isLoading,
}) {
  const chartData = useMemo(() => {
    if (!fromDate || !toDate) return [];

    let days;
    try {
      days = eachDayOfInterval({
        start: parseISO(fromDate),
        end: parseISO(toDate),
      });
    } catch {
      return [];
    }

    const actByDate = {};
    activities.forEach((a) => {
      const d = getDateStr(a);
      if (d) actByDate[d] = (actByDate[d] || 0) + 1;
    });

    const callByDate = {};
    calls.forEach((c) => {
      const d = getDateStr(c);
      if (d) callByDate[d] = (callByDate[d] || 0) + 1;
    });

    return days.map((day) => {
      const key = format(day, "yyyy-MM-dd");
      return {
        date: format(day, days.length > 14 ? "MM/dd" : "EEE dd"),
        Activities: actByDate[key] || 0,
        Calls: callByDate[key] || 0,
      };
    });
  }, [activities, calls, fromDate, toDate]);

  return (
    <Card sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", height: "100%" }}>
      <CardContent sx={{ p: 2.5, pb: "12px !important" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: "0.95rem" }}>
            Activity Overview
          </Typography>
          <Box display="flex" gap={1.5}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: COLORS.indigo }} />
              <Typography variant="caption" color="text.secondary">Calls</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: COLORS.green }} />
              <Typography variant="caption" color="text.secondary">Activities</Typography>
            </Box>
          </Box>
        </Box>

        {isLoading ? (
          <Skeleton variant="rectangular" height={220} sx={{ borderRadius: "10px" }} />
        ) : chartData.length === 0 ? (
          <Box
            height={220}
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ background: "#fafafa", borderRadius: "10px" }}
          >
            <Typography variant="body2" color="text.secondary">
              No data for selected range
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gradCalls" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.indigo} stopOpacity={0.18} />
                  <stop offset="95%" stopColor={COLORS.indigo} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradActivities" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.green} stopOpacity={0.18} />
                  <stop offset="95%" stopColor={COLORS.green} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: "#999" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#999" }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="Calls"
                stroke={COLORS.indigo}
                strokeWidth={2.5}
                fill="url(#gradCalls)"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
              />
              <Area
                type="monotone"
                dataKey="Activities"
                stroke={COLORS.green}
                strokeWidth={2.5}
                fill="url(#gradActivities)"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
